import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Grid, Stack, Chip,
  Card, CardContent, Divider, Alert,
} from '@mui/material';
import {
  Storage, Memory, Router as RouterIcon, Cloud, DeveloperMode,
  Shield, ArrowForward,
} from '@mui/icons-material';
import { C, Section } from './theme';

const plans = [
  {
    id: 'nvme2', name: 'NVMe 2 VPS Hosting', badge: null,
    desc: 'Personal sites, light apps, and dev sandboxes',
    price: '₹1,415', save: 'Save 55%',
    term: 'For 24 month term, Due Today: ₹33,960',
    renew: 'Renews at ₹3,144/mo',
    specsText: '1 vCPU Core · 2 GB DDR5 RAM · 50GB NVMe SSD · Unmetered Bandwidth',
    features: [
      '1 vCPU Core & 2 GB DDR5 RAM',
      '50 GB High-Throughput NVMe Storage',
      'Full Root SSH & API Access',
      'Claude Code Pre-installed',
      'DDoS Protection & Dedicated IP',
      'Unmetered Bandwidth & 99.99% Uptime',
    ],
    specs: [
      { icon: <DeveloperMode fontSize="small" />, text: '1 vCPU Core' },
      { icon: <Memory fontSize="small" />, text: '2 GB DDR5 RAM' },
      { icon: <Storage fontSize="small" />, text: '50GB NVMe Storage' },
      { icon: <RouterIcon fontSize="small" />, text: 'Unmetered Bandwidth' },
      { icon: <Cloud fontSize="small" />, text: 'Multiple data centers' },
    ],
  },
  {
    id: 'nvme4', name: 'NVMe 4 VPS Hosting', badge: null,
    desc: 'Docker, AI agents, and growing applications',
    price: '₹2,830', save: 'Save 55%',
    term: 'For 24 month term, Due Today: ₹67,920',
    renew: 'Renews at ₹6,289/mo',
    specsText: '2 vCPU Cores · 4 GB DDR5 RAM · 100GB NVMe SSD · Unmetered Bandwidth',
    features: [
      '2 vCPU Cores & 4 GB DDR5 RAM',
      '100 GB High-Throughput NVMe Storage',
      'Full Root SSH & API Access',
      'Claude Code Pre-installed',
      'DDoS Protection & Dedicated IP',
      'Unmetered Bandwidth & 99.99% Uptime',
    ],
    specs: [
      { icon: <DeveloperMode fontSize="small" />, text: '2 vCPU Cores' },
      { icon: <Memory fontSize="small" />, text: '4 GB DDR5 RAM' },
      { icon: <Storage fontSize="small" />, text: '100GB NVMe Storage' },
      { icon: <RouterIcon fontSize="small" />, text: 'Unmetered Bandwidth' },
      { icon: <Cloud fontSize="small" />, text: 'Multiple data centers' },
    ],
  },
  {
    id: 'nvme8', name: 'NVMe 8 VPS Hosting', badge: 'RECOMMENDED',
    desc: 'Multi-container production and heavy traffic',
    price: '₹5,667', save: 'Save 55%',
    term: 'For 24 month term, Due Today: ₹1,36,008',
    renew: 'Renews at ₹12,593/mo',
    specsText: '4 vCPU Cores · 8 GB DDR5 RAM · 200GB NVMe SSD · Unmetered Bandwidth',
    features: [
      '4 vCPU Cores & 8 GB DDR5 RAM',
      '200 GB High-Throughput NVMe Storage',
      'Full Root SSH & API Access',
      'Claude Code Pre-installed',
      'DDoS Protection & Dedicated IP',
      'Unmetered Bandwidth & 99.99% Uptime',
    ],
    specs: [
      { icon: <DeveloperMode fontSize="small" />, text: '4 vCPU Cores' },
      { icon: <Memory fontSize="small" />, text: '8 GB DDR5 RAM' },
      { icon: <Storage fontSize="small" />, text: '200GB NVMe Storage' },
      { icon: <RouterIcon fontSize="small" />, text: 'Unmetered Bandwidth' },
      { icon: <Cloud fontSize="small" />, text: 'Multiple data centers' },
    ],
  },
  {
    id: 'nvme16', name: 'NVMe 16 VPS Hosting', badge: null,
    desc: 'Heavy AI workloads, large databases, and enterprise stacks',
    price: '₹11,976', save: 'Save 55%',
    term: 'For 24 month term, Due Today: ₹2,87,424',
    renew: 'Renews at ₹26,613/mo',
    specsText: '8 vCPU Cores · 16 GB DDR5 RAM · 450GB NVMe SSD · Unmetered Bandwidth',
    features: [
      '8 vCPU Cores & 16 GB DDR5 RAM',
      '450 GB High-Throughput NVMe Storage',
      'Full Root SSH & API Access',
      'Claude Code Pre-installed',
      'DDoS Protection & Dedicated IP',
      'Unmetered Bandwidth & 99.99% Uptime',
    ],
    specs: [
      { icon: <DeveloperMode fontSize="small" />, text: '8 vCPU Cores' },
      { icon: <Memory fontSize="small" />, text: '16 GB DDR5 RAM' },
      { icon: <Storage fontSize="small" />, text: '450GB NVMe Storage' },
      { icon: <RouterIcon fontSize="small" />, text: 'Unmetered Bandwidth' },
      { icon: <Cloud fontSize="small" />, text: 'Multiple data centers' },
    ],
  },
];

const VpsPricing = () => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);

  const handleSelectPlan = (plan) => {
    const serializablePlan = {
      id: plan.id,
      name: plan.name,
      badge: plan.badge,
      desc: plan.desc,
      price: plan.price,
      save: plan.save,
      term: plan.term,
      renew: plan.renew,
      specsText: plan.specsText,
      features: plan.features,
      category: 'Self-Managed VPS',
    };
    navigate('/checkout', { state: { plan: serializablePlan } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Section id="pricing" sx={{ background: '#FFFFFF', borderBottom: `1px solid ${C.border}` }}>
      <Container maxWidth="lg">

        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip label="NEW: Claude Code pre-installed on every plan" size="small" sx={{
            background: C.primaryLight, color: C.primary, fontWeight: 700,
            border: `1px solid ${C.primary}30`, fontSize: '0.76rem', mb: 2.5,
          }} />
          <Typography variant="h2" sx={{
            fontWeight: 800, fontSize: { xs: '1.85rem', md: '2.5rem' },
            color: C.textPrimary, letterSpacing: '-0.03em', mb: 1.5,
          }}>
            Choose Your Self-Managed VPS Hosting Plan
          </Typography>
          <Typography sx={{ color: C.textSecondary, maxWidth: 620, mx: 'auto', fontSize: '1.02rem', lineHeight: 1.6 }}>
            Modern, high-performance hardware for developers who want full control —
            with Claude Code ready to go the moment you SSH in.
          </Typography>

          {/* Quick Filter Badges */}
          <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" sx={{ mt: 3 }}>
            {['Claude Code · Agentic Coding Tool', 'n8n · Workflows', 'OpenClaw · AI agents'].map((b) => (
              <Chip key={b} label={b} size="small" sx={{
                background: C.bgSection, border: `1px solid ${C.border}`,
                color: C.textPrimary, fontSize: '0.78rem', fontWeight: 600,
              }} />
            ))}
          </Stack>
        </Box>

        {/* Pricing Cards Grid (MUI v6 Grid size) */}
        <Grid container spacing={3} alignItems="stretch">
          {plans.map((plan) => {
            const isRec = plan.badge === 'RECOMMENDED';
            const isHov = hovered === plan.id;
            return (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={plan.id}>
                <Card
                  onMouseEnter={() => setHovered(plan.id)}
                  onMouseLeave={() => setHovered(null)}
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex', flexDirection: 'column',
                    borderRadius: '16px',
                    border: isRec ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                    boxShadow: isRec || isHov
                      ? '0 12px 30px rgba(37,99,235,0.12)'
                      : '0 2px 10px rgba(0,0,0,0.03)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    transform: isHov ? 'translateY(-5px)' : 'none',
                    position: 'relative', overflow: 'visible',
                    background: isRec ? '#FAFBFD' : '#FFFFFF',
                  }}
                >
                  {/* Recommended Badge */}
                  {isRec && (
                    <Box sx={{
                      position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                      background: C.primary, color: '#FFFFFF', fontWeight: 800,
                      fontSize: '0.7rem', letterSpacing: '0.1em',
                      px: 2.5, py: 0.6, borderRadius: '20px', whiteSpace: 'nowrap',
                    }}>
                      RECOMMENDED
                    </Box>
                  )}

                  <CardContent sx={{ p: 3.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: C.textPrimary, mb: 0.5 }}>
                      {plan.name}
                    </Typography>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.82rem', lineHeight: 1.5, mb: 2.5, minHeight: 40 }}>
                      {plan.desc}
                    </Typography>

                    {/* Price Block */}
                    <Box sx={{ mb: 1 }}>
                      <Stack direction="row" alignItems="baseline" spacing={0.5}>
                        <Typography sx={{ fontWeight: 900, fontSize: '2.5rem', color: C.textPrimary, lineHeight: 1 }}>
                          {plan.price}
                        </Typography>
                        <Typography sx={{ color: C.textMuted, fontSize: '0.88rem', fontWeight: 600 }}>/mo</Typography>
                      </Stack>
                      <Chip label={plan.save} size="small" sx={{
                        mt: 1, background: C.successLight, color: C.success,
                        fontWeight: 700, fontSize: '0.72rem', border: `1px solid ${C.success}40`,
                      }} />
                    </Box>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.74rem', mb: 0.3 }}>{plan.term}</Typography>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.74rem', mb: 2.5 }}>{plan.renew}</Typography>

                    <Divider sx={{ borderColor: C.border, mb: 2.5 }} />

                    {/* Specifications List */}
                    <Stack spacing={1.3} sx={{ mb: 3.5, flex: 1 }}>
                      {plan.specs.map((s) => (
                        <Stack key={s.text} direction="row" spacing={1.5} alignItems="center">
                          <Box sx={{ color: C.primary, display: 'flex' }}>{s.icon}</Box>
                          <Typography sx={{ color: C.textSecondary, fontSize: '0.86rem', fontWeight: 500 }}>
                            {s.text}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    {/* Get Started Button -> Navigates to Checkout Order Detail Page */}
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForward />}
                      onClick={() => handleSelectPlan(plan)}
                      sx={{
                        background: isRec ? C.primary : '#1E293B',
                        color: '#FFFFFF', fontWeight: 700, fontSize: '0.9rem',
                        py: 1.4, borderRadius: '8px', textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          background: isRec ? C.primaryHover : '#0F172A',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Get Started
                    </Button>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.72rem', textAlign: 'center', mt: 1 }}>
                      Instant Setup & Cashfree.com Checkout
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Informational Alert Box */}
        <Alert severity="info" icon={false} sx={{
          mt: 5, background: C.primaryLight, border: `1px solid ${C.primary}25`,
          borderRadius: '12px', color: C.textPrimary, fontSize: '0.85rem',
          maxWidth: 800, mx: 'auto', textAlign: 'center',
        }}>
          💡 <strong>Heads up:</strong> All VPS tiers include root SSH + API access. cPanel and 24/7 priority support
          are not included with VPS plans. Need a different size? Select NVMe 2, 4, 8, or 16.
        </Alert>

      </Container>
    </Section>
  );
};

export default VpsPricing;
